import React, { useState } from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Article as ArticleIcon,
  Description as DescriptionIcon,
  ExpandMore,
  Close as CloseIcon,
  Launch as LaunchIcon,
} from '@mui/icons-material';
import { MDRCitation } from '../services/api';

interface MDRCitationProps {
  citation: MDRCitation;
  onExpand?: () => void;
}

const MDRCitationChip: React.FC<MDRCitationProps> = ({ citation, onExpand }) => {
  const [expanded, setExpanded] = useState(false);

  const getCitationUrl = () => {
    const baseUrl = 'https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:32017R0745';
    if (citation.type === 'Article') {
      return `${baseUrl}#art_${citation.number}`;
    } else if (citation.type === 'Annex') {
      return `${baseUrl}#annex_${citation.number}`;
    } else if (citation.type === 'Chapter') {
      return `${baseUrl}#chapter_${citation.number}`;
    }
    return baseUrl;
  };

  const getIcon = () => {
    switch (citation.type) {
      case 'Article':
        return <ArticleIcon sx={{ fontSize: 16 }} />;
      case 'Annex':
      case 'Chapter':
      case 'Section':
        return <DescriptionIcon sx={{ fontSize: 16 }} />;
      default:
        return <DescriptionIcon sx={{ fontSize: 16 }} />;
    }
  };

  const handleClick = () => {
    setExpanded(!expanded);
    if (onExpand && !expanded) {
      onExpand();
    }
  };

  return (
    <>
      <Chip
        icon={getIcon()}
        label={citation.full_reference}
        onClick={handleClick}
        size="small"
        sx={{
          m: 0.25,
          cursor: 'pointer',
          bgcolor: '#e3f2fd',
          color: '#1565c0',
          border: '1px solid #90caf9',
          '&:hover': {
            bgcolor: '#bbdefb',
            borderColor: '#64b5f6',
          },
          fontSize: '0.75rem',
          height: '24px',
        }}
      />
      
      {expanded && (
        <Dialog
          open={expanded}
          onClose={() => setExpanded(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              maxHeight: '80vh',
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            pb: 1,
            borderBottom: '1px solid #eee'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getIcon()}
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {citation.full_reference}
                {citation.regulation && ` - Regulation (EU) ${citation.regulation}`}
              </Typography>
            </Box>
            <IconButton onClick={() => setExpanded(false)} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          
          <DialogContent sx={{ py: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Link
                href={getCitationUrl()}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: '#1976d2',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                <LaunchIcon sx={{ fontSize: 16 }} />
                View on EUR-Lex
              </Link>
            </Box>

            {citation.context && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Context from Document
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6 }}>
                    {citation.context}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            )}

            {(citation.full_text || citation.source_chunk) && (
              <Accordion sx={{ mt: 1 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Full Legal Text
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" sx={{ color: '#444', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                    {citation.full_text || citation.source_chunk}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            )}

            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="caption" sx={{ color: '#666', fontStyle: 'italic' }}>
                Note: This citation was extracted from the regulatory documents in our database. 
                For the complete official text, please refer to the EUR-Lex link above.
              </Typography>
            </Box>
          </DialogContent>
          
          <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #eee' }}>
            <Button onClick={() => setExpanded(false)} variant="contained" size="small">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

interface MDRCitationListProps {
  citations: MDRCitation[];
}

export const MDRCitationList: React.FC<MDRCitationListProps> = ({ citations }) => {
  if (!citations || citations.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 2, mb: 1, p: 1.5, bgcolor: '#f8f9fa', borderRadius: 1, border: '1px solid #e9ecef' }}>
      <Typography variant="caption" sx={{ fontWeight: 600, color: '#495057', mb: 1, display: 'block' }}>
        MDR Section References:
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {citations.map((citation, idx) => (
          <MDRCitationChip key={idx} citation={citation} />
        ))}
      </Box>
    </Box>
  );
};

export default MDRCitationChip;

